import { useState, useEffect, useRef } from "react"
import axios from "axios"

import ErrorMessage from "components/forms/error-message"
import lodash from "lodash"
import deepdash from "deepdash"
import { lang } from "config"
import Label from "components/forms/label"
import Input from "components/forms/input"
import Description from "components/forms/description"
import nProgress from "nprogress"
import toast from "react-hot-toast"

/** =========================
 *  Async Product Unit Picker (terfilter outlet asal)
 * ========================= */
function AsyncProductUnitPicker({
  disabled,
  value,
  onChange,              // (id, row)
  outlet_group_id,
  outlet_id,             // wajib diisi agar produk terfilter outlet asal
  initialLabel
}) {
  const INV = process.env.REACT_APP_INVENTORY_API_URL
  const perPage = 30
  const [query, setQuery]     = useState("")
  const [page, setPage]       = useState(1)
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [label, setLabel]     = useState(initialLabel || "")
  const [byId, setById]       = useState(new Map())
  const listRef = useRef(null)
  const debounceRef = useRef()

  const buildURL = (params) => {
    const url = new URL(`${INV}/ps_list`)
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v)
    })
    return url.toString()
  }

  const fetchPage = async (p = 1, append = false) => {
    if (!INV) { toast.error("REACT_APP_INVENTORY_API_URL belum diset"); return }
    if (!outlet_id) return // pastikan outlet asal sudah ada
    setLoading(true)
    try {
      const url = buildURL({
        page: p,
        per_page: perPage,
        search: query,
        outlet_group_id,
        outlet_id,
        only_in_stock: true,
        with_attributes: false,
      })
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const rows = Array.isArray(json?.data) ? json.data : []
      const newMap = new Map(byId)
      rows.forEach(r => {
        const id = r.product_unit_id ?? r.id
        if (id) newMap.set(id, r)
      })
      setById(newMap)
      setItems(prev => append ? [...prev, ...rows] : rows)
      setHasMore(Boolean(json?.meta?.next_page_url) || rows.length === perPage)
      setPage(p)
    } catch {
      toast.error("Gagal mengambil daftar produk.")
    } finally {
      setLoading(false)
    }
  }

  const fetchById = async (id) => {
    if (!id || !outlet_id) return null
    if (byId.has(id)) return byId.get(id)
    try {
      const url = buildURL({
        page: 1,
        per_page: 1,
        product_unit_id: id,
        with_attributes: 0,
        only_in_stock: 0,
        outlet_group_id,
        outlet_id,
      })
      const res = await fetch(url)
      if (!res.ok) return null
      const json = await res.json()
      const row = Array.isArray(json?.data) ? json.data[0] : null
      if (row) {
        const m = new Map(byId)
        const pk = row.product_unit_id ?? row.id
        if (pk) m.set(pk, row)
        setById(m)
        return row
      }
    } catch {}
    return null
  }

  // initial & on outlet change
  useEffect(() => { fetchPage(1, false) }, [outlet_id]) // eslint-disable-line

  // debounce search
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPage(1, false), 300)
    return () => clearTimeout(debounceRef.current)
  }, [query]) // eslint-disable-line

  // set label if value exists
  useEffect(() => {
    (async () => {
      if (!value || !outlet_id) return
      const cached = byId.get(value)
      const row = cached || await fetchById(value)
      if (row) setLabel(row.product_unit_long_name || row.product_unit || row.name || row.sku || String(value))
    })()
    // eslint-disable-next-line
  }, [value, outlet_id])

  const onScroll = (e) => {
    const el = e.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (disabled || !outlet_id) {
    return <Input disabled value={label || "Pilih outlet asal dulu"} id="product_unit_label" />
  }

  return (
    <div className="space-y-2">
      <input
        className="w-full px-3 py-2 text-sm border rounded-xl"
        placeholder="Cari SKU / Nama / Brand / Model…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div ref={listRef} className="h-72 overflow-auto border rounded-xl" onScroll={onScroll}>
        {items.map(row => {
          const id  = row.product_unit_id ?? row.id
          const sku = row.sku ?? row.product_code
          const nm  = row.product_unit ?? row.product_unit_long_name ?? row.name
          const brand = row.product_brand ?? row.brand ?? "-"
          const price = Number(row.price ?? row.maximum_retail_price ?? 0)
          const stock = row.current_stock ?? row.stock ?? 0
          const preOrder = row.pre_order === 1 || row.pre_order === true
          return (
            <button
              type="button"
              key={id}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-neutral-50 ${value === id ? 'bg-neutral-100' : ''}`}
              onClick={() => {
                const label = row.product_unit_long_name || row.product_unit || row.name || sku || id
                setLabel(label)
                onChange(id, row)
              }}
            >
              <div className="font-medium">{nm || "-"}</div>
              <div className="text-neutral-500">
                {sku || "-"} • {brand} • {price.toLocaleString('id-ID')}
              </div>
              {preOrder
                ? <div className="text-[10px] text-amber-600">PRE ORDER</div>
                : <div className="text-[10px] text-green-600">Stock: {stock}</div>}
            </button>
          )
        })}
        {loading && <div className="p-3 text-xs text-neutral-500">Memuat…</div>}
        {!loading && items.length === 0 && <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>}
      </div>
    </div>
  )
}

/** =========================
 *  FORM
 * ========================= */
export default function Form({ data, errors = {}, onSubmit }) {
  const _ = deepdash(lodash)

  const id = data?.id

  // values
  const [fromOutletId, setFromOutletId]   = useState(data?.from_outlet_id ?? "")
  const [productUnitId, setProductUnitId] = useState(data?.product_unit_id ?? "")
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [toOutletId, setToOutletId]       = useState(data?.to_outlet_id ?? "")
  const [qty, setQty]                     = useState(data?.qty ?? "")

  // branches (untuk asal & tujuan)
  const GEN = process.env.REACT_APP_GENERIC_API_URL
  const [branches, setBranches] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(false)

  // ---- Fetch all branches ----
  const fetchBranches = async () => {
    setLoadingBranches(true)
    try {
      const { data } = await axios.get(`${GEN}/branch`)
      const list = data?.data ?? data?.branches ?? data ?? []
      setBranches(Array.isArray(list.data) ? list.data : [])
    } catch {
      toast.error("Gagal memuat outlet")
    } finally {
      setLoadingBranches(false)
    }
  }
  useEffect(() => { fetchBranches() }, []) // initial

  const branchOptionLabel = (b) => (b?.name ?? b?.branch_name ?? b?.code ?? b?.branch_code ?? b?.id ?? "")
  const branchOptionValue = (b) => (b?.id ?? b?.branch_id ?? b?.code ?? b?.branch_code ?? "")

  // ganti outlet asal ⇒ reset produk terpilih
  useEffect(() => {
    setProductUnitId("")
    setSelectedProduct(null)
  }, [fromOutletId])

  // setelah pilih produk, pastikan info stoknya sesuai outlet asal (picker sudah terfilter, jadi biasanya sudah benar)
  useEffect(() => {
    // optional: bisa fetch detail lagi kalau perlu
  }, [productUnitId, fromOutletId])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!nProgress.isStarted()) {
      if (!fromOutletId || !productUnitId || !toOutletId || !qty) {
        toast.error("Lengkapi semua field yang wajib")
        return
      }
      if (String(fromOutletId) === String(toOutletId)) {
        toast.error("Outlet asal & tujuan tidak boleh sama")
        return
      }
        const fromOutletName = findBranchNameById(fromOutletId)
        const toOutletName   = findBranchNameById(toOutletId)
      onSubmit({
        id,
        product_unit_id: productUnitId,
        from_outlet_id: fromOutletId,
        to_outlet_id: toOutletId,
        from_outlet_name: fromOutletName,
        to_outlet_name: toOutletName,
        qty
      })
    } else {
      toast.error("Previous action is loading")
    }
  }

  const findBranchNameById = (id) => {
    if (!id) return ""
    const b = branches.find(
        (x) => String(branchOptionValue(x)) === String(id)
    )
    return b ? branchOptionLabel(b) : `OUTLET ${id}`
    }

  const INV = process.env.REACT_APP_INVENTORY_API_URL

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
      <div className="mt-10 sm:mt-0">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
              <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
            </div>
          </div>

          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* 1) OUTLET ASAL */}
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="from_outlet_id" value={`Outlet Asal ${loadingBranches ? '(memuat...)' : ''}`} />
                    <select
                      id="from_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent"
                      value={fromOutletId}
                      onChange={(e) => setFromOutletId(e.target.value)}
                    >
                      <option value="">— Pilih Outlet Asal —</option>
                      {branches.map((b) => {
                        const val = branchOptionValue(b)
                        return <option key={`from-${val}`} value={val}>{branchOptionLabel(b)}</option>
                      })}
                    </select>
                    <ErrorMessage error={errors.from_outlet_id} />
                  </div>

                  {/* 2) PRODUCT UNIT (aktif setelah outlet asal dipilih) */}
                  <div className="col-span-2">
                    <Label htmlFor="product_unit" value={`${lang.product_unit ?? 'Product Unit'}`} />
                    <AsyncProductUnitPicker
                      disabled={!fromOutletId}
                      value={productUnitId}
                      initialLabel={selectedProduct?.product_unit_long_name || selectedProduct?.product_unit}
                      outlet_group_id={data?.outlet_group_id}
                      outlet_id={fromOutletId}            // filter by outlet asal
                      onChange={(id, row) => {
                        setProductUnitId(id)
                        setSelectedProduct(row || null)
                      }}
                    />
                    <Description error={errors.product_unit_id} />
                    {selectedProduct && (
                      <div className="mt-2 text-[11px] text-neutral-600">
                        SKU: <b>{selectedProduct.sku || "-"}</b> •
                        Brand: <b>{selectedProduct.product_brand || "-"}</b> •
                        Stock di outlet asal: <b>{selectedProduct.current_stock ?? "-"}</b>
                      </div>
                    )}
                  </div>

                  {/* 3) QTY */}
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="qty" value="Qty" />
                    <Input
                      type="number"
                      min={1}
                      placeholder="0"
                      onChange={(e) => setQty(e.target.value)}
                      value={qty}
                      id="qty"
                      error={errors.qty}
                    />
                    <ErrorMessage error={errors.qty} />
                  </div>

                  {/* 4) OUTLET TUJUAN */}
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="to_outlet_id" value="Outlet Tujuan" />
                    <select
                      id="to_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent"
                      value={toOutletId}
                      onChange={(e) => setToOutletId(e.target.value)}
                    >
                      <option value="">— Pilih Outlet Tujuan —</option>
                      {branches
                        .filter(b => String(branchOptionValue(b)) !== String(fromOutletId))
                        .map((b) => {
                          const val = branchOptionValue(b)
                          return <option key={`dst-${val}`} value={val}>{branchOptionLabel(b)}</option>
                        })}
                    </select>
                    <ErrorMessage error={errors.to_outlet_id} />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="mt-8 text-xs text-right">
        <button type="submit" className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
          <span>{`${_.isEmpty(data) ? lang.create : lang.update}`}</span>
        </button>
      </div>
    </form>
  )
}
